import React, { useState } from 'react';
import { Table, Button, Row, Col, Form } from 'react-bootstrap';
import { FaEdit, FaTrash, FaDownload } from 'react-icons/fa';
import { Link, useParams } from 'react-router-dom';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import Paginate from '../../components/Paginate';
import {
  useGetPaymentsQuery,
  useDeletePaymentMutation,
  
} from '../../slices/paymentsApiSlice';
import { toast } from 'react-toastify';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';


const PaymentListScreen = () => {
  const { pageNumber } = useParams() || 1;

  const { data, isLoading, error, refetch } = useGetPaymentsQuery({ pageNumber });
  const [deletePayment, { isLoading: loadingDelete }] = useDeletePaymentMutation();
  //const [createPayment, { isLoading: loadingCreate }] = useCreatePaymentMutation();

  const [filter, setFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Create a new payment
  /*const createPaymentHandler = async () => {
    if (window.confirm('Are you sure you want to create a new payment?')) {
      try {
        const newPayment = {
          date: new Date().toISOString().split('T')[0],
          account: 'Debit',
          accountName: 'Default Account',
          subAccount: 'Default Sub Account',
          details: 'New Payment',
          amount: 0,
          status: 'Pending',
          category: 'Other',
          notes: 'Default note',
        
        };
        await createPayment(newPayment).unwrap();
        toast.success('New payment created successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };*/

  // Delete an payment
  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this payment?')) {
      try {
        await deletePayment(id).unwrap();
        toast.success('Payment deleted successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(order);
  };

  // Filter and sort payments
  const filterAndSortPayments = () => {
    let filteredPayments = [...(data?.payments || [])];

    // Filter by text input
    if (filter) {
      filteredPayments = filteredPayments.filter(
        (payment) =>
          payment.account?.toLowerCase().includes(filter.toLowerCase()) ||
          payment.accountName?.toLowerCase().includes(filter.toLowerCase()) ||
          payment.details?.toLowerCase().includes(filter.toLowerCase()) ||
          payment.category?.toLowerCase().includes(filter.toLowerCase()) ||
          payment.subAccount?.toLowerCase().includes(filter.toLowerCase()) ||
          payment.notes?.toLowerCase().includes(filter.toLowerCase()) ||
          payment.username?.toLowerCase().includes(filter.toLowerCase())
      );
    }

    // Filter by date range
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      filteredPayments = filteredPayments.filter((payment) => {
        const paymentDate = new Date(payment.date);
        return paymentDate >= start && paymentDate <= end;
      });
    }

    // Sort payments
    if (sortField) {
      filteredPayments.sort((a, b) => {
        if (sortField === 'amount') {
          return sortOrder === 'asc' ? a.amount - b.amount : b.amount - a.amount;
        } else if (sortField === 'date') {
          return sortOrder === 'asc'
            ? new Date(a.date) - new Date(b.date)
            : new Date(b.date) - new Date(a.date);
        } else {
          const aValue = a[sortField]?.toLowerCase() || '';
          const bValue = b[sortField]?.toLowerCase() || '';
          return sortOrder === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
      });
    }

    return filteredPayments;
  };

  // Calculate total for date range
  const calculateTotalForDateRange = () => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);

    const filteredPayments = data?.payments?.filter((payment) => {
      const paymentDate = new Date(payment.date);
      return paymentDate >= start && paymentDate <= end;
    }) || [];

    return filteredPayments.reduce((acc, payment) => acc + payment.amount, 0);
  };

  // Calculate total for all payments
  const totalAmount = data?.payments?.reduce((acc, payment) => acc + payment.amount, 0) || 0;

  const downloadPaymentImages = async (payment) => {
        const zip = new JSZip();
        const folder = zip.folder(`Payment_${payment._id}`);
    
        const imagePromises = payment.images.map(async (imagePath, index) => {
          try {
            const response = await fetch(imagePath.replace(/\\/g, '/')); // Fix path formatting
            const blob = await response.blob();
            const urlParts = imagePath.split('/');
            const filename = urlParts[urlParts.length - 1]; // Use original filename
    
            folder.file(filename, blob);
          } catch (error) {
            console.error(`Failed to fetch ${imagePath}`, error);
          }
        });
    
        await Promise.all(imagePromises);
        zip.generateAsync({ type: 'blob' }).then((zipFile) => {
          saveAs(zipFile, `Payment_${payment._id}_Images.zip`);
        });
      };
    
      const downloadAllImages = async () => {
        const zip = new JSZip();
        const folder = zip.folder('All_Payments_Images');
    
        const imagePromises = data.payments.flatMap((payment) =>
          payment.images.map(async (imagePath, index) => {
            try {
              const response = await fetch(imagePath.replace(/\\/g, '/')); // Fix path formatting
              const blob = await response.blob();
              const urlParts = imagePath.split('/');
              const filename = urlParts[urlParts.length - 1];
              folder.file(filename, blob);
              
            } catch (error) {
              console.error(`Failed to fetch ${imagePath}`, error);
            }
          })
        );
    
        await Promise.all(imagePromises);
        zip.generateAsync({ type: 'blob' }).then((zipFile) => {
          saveAs(zipFile, 'All_Payments_Images.zip');
        });
      };
        
      const exportToExcel = () => {
        const paymentsData = data?.payments?.map((payment) => ({
          Id: payment._id,
          Date: new Date(payment.date).toLocaleDateString(),
          PaymentId: payment._id,
          Account: payment.account,
          AccountName: payment.accountName,
          SubAccount: payment.subAccount,
          Details: payment.details,
          Amount: `${payment.amount.toFixed(2)}`,
          Category: payment.category,
          Notes: payment.notes,
          Images: payment.images[0],

        }));
      
        const worksheet = XLSX.utils.json_to_sheet(paymentsData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Payments');
      
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const file = new Blob([excelBuffer], { type: 'application/octet-stream' });
      
        saveAs(file, 'Payments.xlsx');
      };

      const filteredTotalAmount = filterAndSortPayments().reduce(
  (acc, payment) => acc + payment.amount,
  0
       );
  
  return (
    <>
      <Row className="align-items-center">
        <Col>
          <h1>Income and Payment</h1>
        </Col>
        <Col className="text-end">
          <Button className="my-3 mx-2" onClick={downloadAllImages}>
            <FaDownload /> Download All Images
          </Button>
          <Button className="my-3 mx-2" onClick={exportToExcel}>
          <FaDownload /> Export to Excel
          </Button>
          <Link to='/create/payment' className='btn btn-light my-3'>
          Add Data
          </Link>
         </Col>
      </Row>
  
      <Row className="mb-3">
        <Col>
          <h5>Total Amount: {totalAmount.toFixed(2)}</h5>
        </Col>
        
         <Col>
          <h4>
          Total (Filtered): ${filteredTotalAmount.toFixed(2)}{' '}
          <span className="text-muted">({data?.payments?.length} total)</span>
           </h4>
         </Col>

        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Search by account, name, details, category, subAccount, or notes"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>
  
      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="Start Date"
          />
        </Col>
        <Col md={4}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="End Date"
          />
        </Col>
        <Col md={4}>
          <h5>
            Total (Filtered Range): ${calculateTotalForDateRange().toFixed(2)}
          </h5>
        </Col>
      </Row>
  
      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || 'An error occurred'}</Message>
      ) : data?.payments?.length > 0 ? (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th onClick={() => handleSort('date')} style={{ cursor: 'pointer' }}>
                  Date {sortField === 'date' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th>Account</th>
                <th onClick={() => handleSort('accountName')} style={{ cursor: 'pointer' }}>
                  Account Name {sortField === 'accountName' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('details')} style={{ cursor: 'pointer' }}>
                  Details {sortField === 'details' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('amount')} style={{ cursor: 'pointer' }}>
                  Amount {sortField === 'amount' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('category')} style={{ cursor: 'pointer' }}>
                  Category {sortField === 'category' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('notes')} style={{ cursor: 'pointer' }}>
                  Notes {sortField === 'notes' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                
                <th>Images</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filterAndSortPayments().map((payment) => (
                <tr key={payment._id}>
                  <td>{new Date(payment.date).toLocaleDateString()}</td>
                  <td>{payment.account}</td>
                  <td>{payment.accountName}</td>
                  <td>{payment.details}</td>
                  <td>
                    {payment.amount >= 0
                      ? `${payment.amount.toFixed(2)}`
                      : `-${Math.abs(payment.amount).toFixed(2)}`}
                  </td>
                  <td>{payment.category}</td>
                  <td>{payment.notes}</td>
                  
                  <td className="thumbnails">
                    {payment.images?.length > 0 && (
                      <img
                        src={payment.images[0]}
                        alt="Thumbnail"
                        className="thumbnail-img me-2"
                        style={{
                          width: '15px',
                          height: '15px',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </td>

                  <td>
                    
                    <Button
                      as={Link}
                      to={`/admin/payment/${payment._id}/edit`}
                      variant="light"
                      className="btn-sm mx-2"
                    >
                      <FaEdit />
                    </Button>
                    <Link
                       className="btn btn-primary btn-sm m-2"
                      to={`/payment/${payment._id}`}
                        role="button"
                    >
                    Details
                   </Link>
                    <Button
                      variant="danger"
                      className="btn-sm"
                      onClick={() => deleteHandler(payment._id)}
                    >
                      <FaTrash />
                    </Button>
                    <Button variant="info" className="btn-sm" onClick={() => downloadPaymentImages(payment)}>
                      <FaDownload />Images
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Paginate pages={data.pages} page={data.page} isAdmin={true} type="paymentlist" />
        </>
      ) : (
        <Message variant="info">No payments found</Message>
      )}
    </>
  );
};

export default PaymentListScreen;